import React, { useRef } from 'react';
import { Radio, Plus, X, Edit, Trash2, Printer, Save, RefreshCw, ShieldCheck, ShieldAlert, ShieldX, Activity, Download, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API_BASE_URL } from '../../config';
import secureStorage from '../../utils/secureStorage';

/**
 * CommsPlanForm Component
 * Handles the ICS 205 Communications Plan form
 * With real-time collaboration support
 */
const CommsPlanForm = ({
  theme,
  currentExercise,
  // State from useCommsPlan hook
  ics205Frequencies,
  currentIcs205Frequency,
  setCurrentIcs205Frequency,
  editingIcs205Frequency,
  loading,
  lastSaved,
  hasUnsavedChanges,
  saveStatus,
  connectionHealth,
  // Functions from useCommsPlan hook
  addIcs205Frequency,
  editIcs205Frequency,
  cancelEditIcs205Frequency,
  deleteIcs205Frequency,
  printIcs205Combined,
  saveFrequencies,
  refreshFrequencies,
  verifyBackendHealth
}) => {
  const frequencyTypes = [
    'Command',
    'Tactical',
    'Ground to Air',
    'Air to Air',
    'Support',
    'Medical',
    'Logistics',
    'Admin',
    'Other'
  ];

  const radioTypes = [
    'VHF',
    'UHF',
    'HF',
    'Satellite',
    'Satellite Radio',
    'Satellite Phone',
    '800 MHz',
    'P25',
    'Other'
  ];

  // Modulation types categorized
  const modulationTypes = {
    'Analog': ['AM', 'DSB-SC', 'ISB', 'USB', 'LSB', 'AME', 'FM', 'PM'],
    'Digital': ['APCO P25', 'D-STAR', 'DMR', 'NXDN', 'Fusion', 'FreeDV', 'M17', 'Packet', 'PACTOR', 'VARA'],
    'Morse': ['CW', 'MCW', 'FSK']
  };

  // === Comms Freqs CSV Import / Export ===
  const csvFileInputRef = useRef(null);

  const handleExportFreqsCSV = async () => {
    if (!currentExercise?.id) return;
    try {
      const token = secureStorage.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/api/ics-205/${currentExercise.id}/export-csv`, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (currentExercise.exercise_name || currentExercise.name || 'exercise').replace(/[^a-z0-9]+/gi, '_');
      link.setAttribute('download', `ics-205-freqs-${safeName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting ICS 205 CSV:', error);
      window.alert('Failed to export CSV. Please try again.');
    }
  };

  const handleImportFreqsClick = () => {
    if (csvFileInputRef.current) csvFileInputRef.current.click();
  };

  const handleImportFreqsFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentExercise?.id) return;
    try {
      const token = secureStorage.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const payload = new FormData();
      payload.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/ics-205/${currentExercise.id}/import-csv`, {
        method: 'POST',
        headers,
        body: payload,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const result = await response.json();
      window.alert(`Import complete. ${result.created || 0} frequency row(s) added, ${result.updated || 0} updated.`);
      if (typeof refreshFrequencies === 'function') await refreshFrequencies();
    } catch (error) {
      console.error('Error importing ICS 205 CSV:', error);
      window.alert(typeof error.message === 'string' ? error.message : 'Failed to import CSV.');
    }
  };

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Communications Plan Header - Compact */}
      <div className="flex justify-between items-center flex-shrink-0">
        <div className="flex-1">
          <h1 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>ICS 205 - Comms Plan (frequencies)</h1>
          <p className={`${theme.colors.textMuted} text-sm`}>
            Manage radio frequencies and communication assignments
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-gray-600">Auto-syncs every 15s</span>
            {saveStatus === 'saving' && (
              <span className="text-xs text-yellow-400 flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                Saving...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="text-xs text-red-400 flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                Save failed - check connection
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={refreshFrequencies}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-gray-500 text-gray-400 hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportFreqsFile}
            className="hidden"
            data-testid="ics205-import-file"
          />
          <Button
            onClick={handleImportFreqsClick}
            variant="outline"
            size="sm"
            className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
            title="Import frequencies from CSV"
            data-testid="ics205-import-csv"
          >
            <Upload className="h-4 w-4 mr-1" />
            Import CSV
          </Button>
          <Button
            onClick={handleExportFreqsCSV}
            variant="outline"
            size="sm"
            className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
            disabled={!ics205Frequencies || ics205Frequencies.length === 0}
            title="Export frequencies as CSV"
            data-testid="ics205-export-csv"
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button 
            onClick={() => printIcs205Combined()}
            size="sm"
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <Printer className="h-4 w-4 mr-1" />
            Print ICS 205
          </Button>
        </div>
      </div>

      {/* Connection Health Indicator */}
      {(() => {
        const healthBg = { healthy: 'bg-emerald-950/40 border-emerald-700/50', degraded: 'bg-amber-950/40 border-amber-700/50', error: 'bg-red-950/40 border-red-700/50' };
        const healthText = { healthy: 'text-emerald-300', degraded: 'text-amber-300', error: 'text-red-300' };
        const hStatus = connectionHealth?.status;
        return connectionHealth && (
        <div
          data-testid="connection-health-indicator"
          className={`flex items-center justify-between rounded-lg border px-4 py-3 transition-colors ${
            healthBg[hStatus] || 'bg-gray-800/40 border-gray-700/50'
          }`}
        >
          <div className="flex items-center gap-3">
            {connectionHealth.status === 'healthy' && (
              <ShieldCheck data-testid="health-icon-healthy" className="h-5 w-5 text-emerald-400 shrink-0" />
            )}
            {connectionHealth.status === 'degraded' && (
              <ShieldAlert data-testid="health-icon-degraded" className="h-5 w-5 text-amber-400 shrink-0" />
            )}
            {connectionHealth.status === 'error' && (
              <ShieldX data-testid="health-icon-error" className="h-5 w-5 text-red-400 shrink-0" />
            )}
            {connectionHealth.status === 'checking' && (
              <Activity data-testid="health-icon-checking" className="h-5 w-5 text-gray-400 shrink-0 animate-pulse" />
            )}
            <div>
              <span
                data-testid="health-status-text"
                className={`text-sm font-medium ${
                  healthText[hStatus] || 'text-gray-400'
                }`}
              >
                {connectionHealth.status === 'healthy' && 'Backend Healthy'}
                {connectionHealth.status === 'degraded' && 'Backend Degraded'}
                {connectionHealth.status === 'error' && 'Backend Unreachable'}
                {connectionHealth.status === 'checking' && 'Checking Backend...'}
              </span>
              <p data-testid="health-message" className="text-xs text-gray-400 mt-0.5">
                {connectionHealth.message}
              </p>
              {connectionHealth.missingFields?.length > 0 && (
                <p data-testid="health-missing-fields" className="text-xs text-amber-400 mt-0.5">
                  {connectionHealth.status === 'degraded' && connectionHealth.message?.includes('backup active')
                    ? 'Field backup encoding active — data will persist across reloads'
                    : <>Fields not persisting natively: <strong>{connectionHealth.missingFields.join(', ')}</strong> — backup encoding active</>
                  }
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connectionHealth.lastVerifiedAt && (
              <span data-testid="health-last-verified" className="text-xs text-gray-500">
                Checked {new Date(connectionHealth.lastVerifiedAt).toLocaleTimeString()}
              </span>
            )}
            <Button
              data-testid="health-recheck-btn"
              onClick={verifyBackendHealth}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white h-7 px-2"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
      )}

      {/* Frequency Entry Form */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader className="py-2">
          <CardTitle className="text-orange-500 text-base">Add Radio Frequency</CardTitle>
          <CardDescription className={`${theme.colors.textMuted} text-xs`}>
            Enter frequency details and radio information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-2">
          {/* Row 1: Zone Grp, Ch #, Function, Channel Name, Assignment */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Zone Grp</label>
              <Input
                type="text"
                value={currentIcs205Frequency.zone_grp || ''}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, zone_grp: e.target.value}))}
                placeholder="e.g., Zone 1"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Ch #</label>
              <Input
                type="number"
                value={currentIcs205Frequency.channel}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, channel: e.target.value}))}
                placeholder="e.g., 1"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Function</label>
              <Select 
                value={currentIcs205Frequency.type || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, type: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select function" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {frequencyTypes.map(type => (
                    <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Channel Name</label>
              <Input
                type="text"
                value={currentIcs205Frequency.channel_name || ''}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, channel_name: e.target.value}))}
                placeholder="e.g., Command 1"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Assignment</label>
              <Input
                type="text"
                value={currentIcs205Frequency.description}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, description: e.target.value}))}
                placeholder="Enter assignment"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Row 2: RX Freq (N/W), RX Tone/NAC, TX Freq (N/W), TX Tone/NAC */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-white text-xs font-medium mb-1 block">RX Freq (N/W)</label>
              <Input
                type="text"
                value={currentIcs205Frequency.rx_freq || ''}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, rx_freq: e.target.value}))}
                placeholder="e.g., 154.280"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">RX Tone/NAC</label>
              <Select 
                value={currentIcs205Frequency.tone || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, tone: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 max-h-64 overflow-y-auto">
                  <SelectItem value="Simplex" className="text-white focus:bg-gray-600">Simplex</SelectItem>
                  <SelectItem value="67.0" className="text-white focus:bg-gray-600">67.0</SelectItem>
                  <SelectItem value="69.3" className="text-white focus:bg-gray-600">69.3</SelectItem>
                  <SelectItem value="71.9" className="text-white focus:bg-gray-600">71.9</SelectItem>
                  <SelectItem value="74.4" className="text-white focus:bg-gray-600">74.4</SelectItem>
                  <SelectItem value="77.0" className="text-white focus:bg-gray-600">77.0</SelectItem>
                  <SelectItem value="79.7" className="text-white focus:bg-gray-600">79.7</SelectItem>
                  <SelectItem value="82.5" className="text-white focus:bg-gray-600">82.5</SelectItem>
                  <SelectItem value="85.4" className="text-white focus:bg-gray-600">85.4</SelectItem>
                  <SelectItem value="88.5" className="text-white focus:bg-gray-600">88.5</SelectItem>
                  <SelectItem value="91.5" className="text-white focus:bg-gray-600">91.5</SelectItem>
                  <SelectItem value="94.8" className="text-white focus:bg-gray-600">94.8</SelectItem>
                  <SelectItem value="97.4" className="text-white focus:bg-gray-600">97.4</SelectItem>
                  <SelectItem value="100.0" className="text-white focus:bg-gray-600">100.0</SelectItem>
                  <SelectItem value="103.5" className="text-white focus:bg-gray-600">103.5</SelectItem>
                  <SelectItem value="107.2" className="text-white focus:bg-gray-600">107.2</SelectItem>
                  <SelectItem value="110.9" className="text-white focus:bg-gray-600">110.9</SelectItem>
                  <SelectItem value="114.8" className="text-white focus:bg-gray-600">114.8</SelectItem>
                  <SelectItem value="118.8" className="text-white focus:bg-gray-600">118.8</SelectItem>
                  <SelectItem value="123.0" className="text-white focus:bg-gray-600">123.0</SelectItem>
                  <SelectItem value="127.3" className="text-white focus:bg-gray-600">127.3</SelectItem>
                  <SelectItem value="131.8" className="text-white focus:bg-gray-600">131.8</SelectItem>
                  <SelectItem value="136.5" className="text-white focus:bg-gray-600">136.5</SelectItem>
                  <SelectItem value="141.3" className="text-white focus:bg-gray-600">141.3</SelectItem>
                  <SelectItem value="146.2" className="text-white focus:bg-gray-600">146.2</SelectItem>
                  <SelectItem value="151.4" className="text-white focus:bg-gray-600">151.4</SelectItem>
                  <SelectItem value="156.7" className="text-white focus:bg-gray-600">156.7</SelectItem>
                  <SelectItem value="162.2" className="text-white focus:bg-gray-600">162.2</SelectItem>
                  <SelectItem value="167.9" className="text-white focus:bg-gray-600">167.9</SelectItem>
                  <SelectItem value="173.8" className="text-white focus:bg-gray-600">173.8</SelectItem>
                  <SelectItem value="179.9" className="text-white focus:bg-gray-600">179.9</SelectItem>
                  <SelectItem value="186.2" className="text-white focus:bg-gray-600">186.2</SelectItem>
                  <SelectItem value="192.8" className="text-white focus:bg-gray-600">192.8</SelectItem>
                  <SelectItem value="203.5" className="text-white focus:bg-gray-600">203.5</SelectItem>
                  <SelectItem value="210.7" className="text-white focus:bg-gray-600">210.7</SelectItem>
                  <SelectItem value="218.1" className="text-white focus:bg-gray-600">218.1</SelectItem>
                  <SelectItem value="225.7" className="text-white focus:bg-gray-600">225.7</SelectItem>
                  <SelectItem value="233.6" className="text-white focus:bg-gray-600">233.6</SelectItem>
                  <SelectItem value="241.8" className="text-white focus:bg-gray-600">241.8</SelectItem>
                  <SelectItem value="250.3" className="text-white focus:bg-gray-600">250.3</SelectItem>
                  <SelectItem value="254.1" className="text-white focus:bg-gray-600">254.1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">TX Freq (N/W)</label>
              <Input
                type="text"
                value={currentIcs205Frequency.frequency}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, frequency: e.target.value}))}
                placeholder="e.g., 154.280"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">TX Tone/NAC</label>
              <Select 
                value={currentIcs205Frequency.tx_tone || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, tx_tone: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 max-h-64 overflow-y-auto">
                  <SelectItem value="Simplex" className="text-white focus:bg-gray-600">Simplex</SelectItem>
                  <SelectItem value="67.0" className="text-white focus:bg-gray-600">67.0</SelectItem>
                  <SelectItem value="69.3" className="text-white focus:bg-gray-600">69.3</SelectItem>
                  <SelectItem value="71.9" className="text-white focus:bg-gray-600">71.9</SelectItem>
                  <SelectItem value="74.4" className="text-white focus:bg-gray-600">74.4</SelectItem>
                  <SelectItem value="77.0" className="text-white focus:bg-gray-600">77.0</SelectItem>
                  <SelectItem value="79.7" className="text-white focus:bg-gray-600">79.7</SelectItem>
                  <SelectItem value="82.5" className="text-white focus:bg-gray-600">82.5</SelectItem>
                  <SelectItem value="85.4" className="text-white focus:bg-gray-600">85.4</SelectItem>
                  <SelectItem value="88.5" className="text-white focus:bg-gray-600">88.5</SelectItem>
                  <SelectItem value="91.5" className="text-white focus:bg-gray-600">91.5</SelectItem>
                  <SelectItem value="94.8" className="text-white focus:bg-gray-600">94.8</SelectItem>
                  <SelectItem value="97.4" className="text-white focus:bg-gray-600">97.4</SelectItem>
                  <SelectItem value="100.0" className="text-white focus:bg-gray-600">100.0</SelectItem>
                  <SelectItem value="103.5" className="text-white focus:bg-gray-600">103.5</SelectItem>
                  <SelectItem value="107.2" className="text-white focus:bg-gray-600">107.2</SelectItem>
                  <SelectItem value="110.9" className="text-white focus:bg-gray-600">110.9</SelectItem>
                  <SelectItem value="114.8" className="text-white focus:bg-gray-600">114.8</SelectItem>
                  <SelectItem value="118.8" className="text-white focus:bg-gray-600">118.8</SelectItem>
                  <SelectItem value="123.0" className="text-white focus:bg-gray-600">123.0</SelectItem>
                  <SelectItem value="127.3" className="text-white focus:bg-gray-600">127.3</SelectItem>
                  <SelectItem value="131.8" className="text-white focus:bg-gray-600">131.8</SelectItem>
                  <SelectItem value="136.5" className="text-white focus:bg-gray-600">136.5</SelectItem>
                  <SelectItem value="141.3" className="text-white focus:bg-gray-600">141.3</SelectItem>
                  <SelectItem value="146.2" className="text-white focus:bg-gray-600">146.2</SelectItem>
                  <SelectItem value="151.4" className="text-white focus:bg-gray-600">151.4</SelectItem>
                  <SelectItem value="156.7" className="text-white focus:bg-gray-600">156.7</SelectItem>
                  <SelectItem value="162.2" className="text-white focus:bg-gray-600">162.2</SelectItem>
                  <SelectItem value="167.9" className="text-white focus:bg-gray-600">167.9</SelectItem>
                  <SelectItem value="173.8" className="text-white focus:bg-gray-600">173.8</SelectItem>
                  <SelectItem value="179.9" className="text-white focus:bg-gray-600">179.9</SelectItem>
                  <SelectItem value="186.2" className="text-white focus:bg-gray-600">186.2</SelectItem>
                  <SelectItem value="192.8" className="text-white focus:bg-gray-600">192.8</SelectItem>
                  <SelectItem value="203.5" className="text-white focus:bg-gray-600">203.5</SelectItem>
                  <SelectItem value="210.7" className="text-white focus:bg-gray-600">210.7</SelectItem>
                  <SelectItem value="218.1" className="text-white focus:bg-gray-600">218.1</SelectItem>
                  <SelectItem value="225.7" className="text-white focus:bg-gray-600">225.7</SelectItem>
                  <SelectItem value="233.6" className="text-white focus:bg-gray-600">233.6</SelectItem>
                  <SelectItem value="241.8" className="text-white focus:bg-gray-600">241.8</SelectItem>
                  <SelectItem value="250.3" className="text-white focus:bg-gray-600">250.3</SelectItem>
                  <SelectItem value="254.1" className="text-white focus:bg-gray-600">254.1</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Radio Type, Offset, Primary/Backup, Modulation Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Radio Type</label>
              <Select 
                value={currentIcs205Frequency.radio_type || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, radio_type: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select radio type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {radioTypes.map(type => (
                    <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Offset</label>
              <Select 
                value={currentIcs205Frequency.offset || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, offset: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select offset" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="Simplex" className="text-white focus:bg-gray-600">Simplex</SelectItem>
                  <SelectItem value="+600 kHz (0.6 MHz)" className="text-white focus:bg-gray-600">+600 kHz (0.6 MHz)</SelectItem>
                  <SelectItem value="-600 kHz (0.6 MHz)" className="text-white focus:bg-gray-600">-600 kHz (0.6 MHz)</SelectItem>
                  <SelectItem value="+5 MHz (5.0 MHz)" className="text-white focus:bg-gray-600">+5 MHz (5.0 MHz)</SelectItem>
                  <SelectItem value="-5 MHz (5.0 MHz)" className="text-white focus:bg-gray-600">-5 MHz (5.0 MHz)</SelectItem>
                  <SelectItem value="+1.6 MHz" className="text-white focus:bg-gray-600">+1.6 MHz</SelectItem>
                  <SelectItem value="-500 kHz (0.5 MHz)" className="text-white focus:bg-gray-600">-500 kHz (0.5 MHz)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Primary/Backup</label>
              <Select 
                value={currentIcs205Frequency.primary_backup || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, primary_backup: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select P/B" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="Primary" className="text-white focus:bg-gray-600">Primary</SelectItem>
                  <SelectItem value="Backup" className="text-white focus:bg-gray-600">Backup</SelectItem>
                  <SelectItem value="Contingency" className="text-white focus:bg-gray-600">Contingency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Modulation Type</label>
              <Select 
                value={currentIcs205Frequency.modulation_type || ""} 
                onValueChange={(value) => setCurrentIcs205Frequency(prev => ({...prev, modulation_type: value}))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select modulation" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 max-h-64 overflow-y-auto">
                  {Object.entries(modulationTypes).map(([category, types]) => (
                    <div key={category}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-orange-400 bg-gray-800 sticky top-0">
                        {category}
                      </div>
                      {types.map(type => (
                        <SelectItem key={type} value={type} className="text-white focus:bg-gray-600 pl-4">
                          {type}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 4: Remarks */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-white text-xs font-medium mb-1 block">Remarks</label>
              <Input
                type="text"
                value={currentIcs205Frequency.remarks || ''}
                onChange={(e) => setCurrentIcs205Frequency(prev => ({...prev, remarks: e.target.value}))}
                placeholder="Enter any additional remarks or notes"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={addIcs205Frequency} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              {editingIcs205Frequency ? 'Update Frequency' : 'Add Frequency'}
            </Button>
            {editingIcs205Frequency && (
              <Button onClick={cancelEditIcs205Frequency} variant="outline" className="border-gray-500 text-gray-300">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Display Frequencies */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-orange-500">Frequency Assignments ({ics205Frequencies.length})</CardTitle>
          <div className="flex gap-2">
            {ics205Frequencies.length > 0 && (
              <Button 
                onClick={() => printIcs205Combined()}
                variant="outline" 
                className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                size="sm"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {ics205Frequencies.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Radio className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No frequencies added yet</p>
              <p className="text-sm">Add your first frequency assignment above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ics205Frequencies.map(freq => {
                // Diagnostic: log what each frequency object contains at RENDER time
                if (freq.zone_grp || freq.channel_name || freq.rx_freq || freq.tx_tone || freq.remarks) {
                  console.info('[CommsPlan RENDER] freq HAS critical fields:', freq.id, {
                    zone_grp: freq.zone_grp, channel_name: freq.channel_name,
                    rx_freq: freq.rx_freq, tx_tone: freq.tx_tone, remarks: freq.remarks
                  });
                } else {
                  console.warn('[CommsPlan RENDER] freq MISSING critical fields:', freq.id, 
                    'All keys:', Object.keys(freq).sort().join(','));
                }
                return (
                <div key={freq.id} className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 hover:bg-gray-800/70">
                  {/* Entry Row 1: All fields except Remarks */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 xl:grid-cols-14 gap-3 mb-3">
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Zone Grp</span>
                      <span className="text-white text-sm">{freq.zone_grp || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Ch #</span>
                      <span className="text-white text-sm">{freq.channel || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Function</span>
                      <span className="text-white text-sm">{freq.type || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Channel Name</span>
                      <span className="text-white text-sm">{freq.channel_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Assignment</span>
                      <span className="text-white text-sm">{freq.description || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">RX Freq</span>
                      <span className="text-white text-sm font-mono">{freq.rx_freq || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">RX Tone/NAC</span>
                      <span className="text-white text-sm">{freq.tone || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">TX Freq</span>
                      <span className="text-white text-sm font-mono">{freq.frequency || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">TX Tone/NAC</span>
                      <span className="text-white text-sm">{freq.tx_tone || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Radio Type</span>
                      <span className="text-white text-sm">{freq.radio_type || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Offset</span>
                      <span className="text-white text-sm">{freq.offset || '-'}</span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">P/B</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        freq.primary_backup === 'Primary' ? 'bg-green-500/20 text-green-400' : 
                        freq.primary_backup === 'Backup' ? 'bg-yellow-500/20 text-yellow-400' : 
                        freq.primary_backup === 'Contingency' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {freq.primary_backup || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-orange-500 text-xs font-semibold block mb-1">Modulation</span>
                      <span className="text-white text-sm">{freq.modulation_type || '-'}</span>
                    </div>
                    <div className="flex justify-end items-start">
                      <div className="flex gap-1">
                        <Button
                          onClick={() => editIcs205Frequency(freq)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300 p-1 h-auto"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteIcs205Frequency(freq.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 p-1 h-auto"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Entry Row 2: Remarks only */}
                  <div>
                    <span className="text-orange-500 text-xs font-semibold block mb-1">Remarks</span>
                    <span className="text-white text-sm">{freq.remarks || '-'}</span>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommsPlanForm;
